/*
 * Card image strip -- arrows over the picture.
 *
 * The strip is a scroll container with snap points, so a swipe on a phone and
 * a trackpad flick on a laptop both work without a line of code. The arrows
 * only exist for the pointer case, where there is no gesture to make.
 *
 * Everything is delegated from the document rather than bound per card: the
 * collection grid appends pages of cards as you scroll, and anything bound at
 * load would leave every card after the first page dead.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function stripOf(node) {
    var media = node.closest('.item__media');
    return media ? media.querySelector('[data-item-strip]') : null;
  }

  /* An arrow pointing at nothing is worse than no arrow -- it invites a click
     that cannot do anything. Hidden at each end rather than dimmed, since the
     pair only appears on hover anyway and a greyed control in a set that just
     faded in reads as broken rather than exhausted. */
  function syncArrows(strip) {
    var media = strip.closest('.item__media');
    if (!media) return;

    var prev = media.querySelector('[data-item-nav="-1"]');
    var next = media.querySelector('[data-item-nav="1"]');
    // 2px of slack: sub-pixel rounding otherwise leaves the last slide
    // reporting a fraction of a pixel still to scroll, so "next" never goes.
    var atStart = strip.scrollLeft <= 2;
    var atEnd = strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - 2;

    if (prev) prev.hidden = atStart;
    if (next) next.hidden = atEnd;
  }

  /* Turn a card's held-back slides into real images.
   *
   * Called on the first sign that someone is interested in this particular
   * card -- a hover, a touch, a focus, an arrow click. Until then the slides
   * are <img> elements with no src, which cost a few hundred bytes of markup
   * each and not one request.
   *
   * Idempotent by construction: the data- attributes are removed as they are
   * read, so the selector finds nothing on a second call and there is no flag
   * to keep in step. */
  /* Its own function, not inline in the loop below: `var` is function-scoped,
     so a `var slide` declared inside the loop is one binding shared by every
     iteration, and each listener would close over whichever slide the loop
     happened to finish on. Measured with that bug in place -- eight images
     decoded, six slides still flagged as loading, one cleared. A call per
     image gives each listener its own scope. */
  function markLoading(img) {
    var slide = img.closest('.item__slide');
    if (!slide) return;

    slide.classList.add('item__slide--loading');

    var settle = function () {
      slide.classList.remove('item__slide--loading');
    };

    // A cached candidate can be complete before the listener is attached.
    if (img.complete && img.naturalWidth > 0) {
      settle();
      return;
    }

    img.addEventListener('load', settle, { once: true });
    img.addEventListener('error', settle, { once: true });
  }

  function promote(node) {
    var media = node.closest ? node.closest('.item__media') : null;
    if (!media) return;

    // The slides after the first four are held in a <template> (item.liquid),
    // which is not part of the live page. Copy them into the strip, in place
    // and in order, before looking for held images. The template is replaced
    // by its contents, so a second call finds nothing to do.
    var more = media.querySelector('template[data-item-more]');
    if (more) more.replaceWith(more.content);

    var held = media.querySelectorAll('img[data-item-deferred]');
    for (var i = 0; i < held.length; i++) {
      var img = held[i];

      // A promoted image is not an instant image. Between the src landing and
      // the bytes arriving the <img> paints nothing, so the slide showed the
      // card's own dark panel -- a blank hole exactly where the picture the
      // person just asked for should be. Marked here rather than in CSS
      // because the deferred attributes are removed a few lines down, so by
      // the time it matters there is nothing left to select on.
      markLoading(img);

      // sizes before srcset: the candidate is chosen when srcset is set, and
      // a srcset without sizes yet resolves against a 100vw default.
      if (img.dataset.sizes) img.sizes = img.dataset.sizes;
      if (img.dataset.srcset) img.srcset = img.dataset.srcset;
      if (img.dataset.src) img.src = img.dataset.src;
      img.removeAttribute('data-item-deferred');
      img.removeAttribute('data-src');
      img.removeAttribute('data-srcset');
      img.removeAttribute('data-sizes');
    }
  }

  /* Every way a person can show interest in one card, delegated. pointerover
     covers hover and the first touch; focusin covers the keyboard; scroll
     covers a swipe that begins before any of the others fire. */
  document.addEventListener('pointerover', function (event) {
    promote(event.target);
  });

  document.addEventListener('focusin', function (event) {
    promote(event.target);
  });

  document.addEventListener('click', function (event) {
    var arrow = event.target.closest('[data-item-nav]');
    if (!arrow) return;

    // The card is a link; this click is for the strip, not for going there.
    event.preventDefault();
    event.stopPropagation();

    var strip = stripOf(arrow);
    if (!strip) return;

    // Before scrolling, so the image is at least requested by the time the
    // scroll animation lands on it.
    promote(arrow);

    strip.scrollBy({
      left: Number(arrow.getAttribute('data-item-nav')) * strip.clientWidth,
      behavior: reduceMotion.matches ? 'auto' : 'smooth'
    });
  });

  // scroll does not bubble, so it is caught on the way down instead.
  document.addEventListener(
    'scroll',
    function (event) {
      var strip = event.target;
      if (strip && strip.hasAttribute && strip.hasAttribute('data-item-strip')) {
        promote(strip);
        syncArrows(strip);
      }
    },
    true
  );
})();
